import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import {
  People as PeopleIcon,
  EventNote as EventNoteIcon,
  AccessTime as AccessTimeIcon,
  Message as MessageIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import PersonnelManagement from './PersonnelManagement';
import TimeTable from './TimeTable';
import LeaveRequests from './LeaveRequests';
import Messages from './Messages';
import AbsenceOverview from './AbsenceOverview';
import { useNavigate } from 'react-router-dom';

const ChefServiceDashboard = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalPersonnel: 0,
    activePersonnel: 0,
    inactivePersonnel: 0,
    recentAbsences: [],
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Récupérer le personnel du chef de service
        const personnelQuery = query(
          collection(db, 'users'),
          where('chefServiceId', '==', currentUser.uid),
          where('role', '==', 'personnel')
        );
        
        const personnelSnapshot = await getDocs(personnelQuery);
        const personnel = personnelSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Calculer les statistiques
        const active = personnel.filter(p => p.status === 'actif').length;
        const inactive = personnel.filter(p => p.status !== 'actif').length;

        setStats({
          totalPersonnel: personnel.length,
          activePersonnel: active,
          inactivePersonnel: inactive,
          recentAbsences: [], // À implémenter avec la collection des absences
        });

      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
      }
    };

    if (currentUser) {
      fetchDashboardData();
    }
  }, [currentUser]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Tableau de Bord - {userData?.department}
      </Typography>
      
      <Grid container spacing={3}>
        {/* Vue d'ensemble des absences */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <EventNoteIcon sx={{ mr: 1 }} />
              Suivi des absences
            </Typography>
            <AbsenceOverview />
          </Paper>
        </Grid>

        {/* Statistiques générales */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PeopleIcon sx={{ mr: 1 }} />
              Vue d'ensemble du Personnel
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Personnel
                    </Typography>
                    <Typography variant="h4">
                      {stats.totalPersonnel}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Personnel Actif
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {stats.activePersonnel}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Personnel Inactif
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {stats.inactivePersonnel}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Informations du département */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Informations du Département
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Département"
                  secondary={userData?.department}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Chef de Service"
                  secondary={`${userData?.firstName} ${userData?.lastName}`}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Email"
                  secondary={userData?.email}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Mes demandes d'absence */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#f5f5f5' }}>
            <AssignmentIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom align="center">
              Mes Demandes d'Absence
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Consultez et imprimez vos demandes d'autorisation d'absence
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<EventNoteIcon />}
              onClick={() => navigate('/chef-service/my-absences')}
            >
              Voir mes demandes
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ChefServiceDashboard;
